import { IAirQuoteFilter } from "./IAirQuoteFilter";
import { ICodeName } from "../model/shared/ICodeName";
import { ITotal } from "libraries/interfaces/ITotal";
import { ITravellerObject } from "../hotel/ISearchParameters";
import { IFareFamily, IPrice } from "libraries/service/resources/booking/interfaces/IGetBookings";
import { IAirRating } from "../../../model/air-quote/IQuote";
import { ICarbonFootPrint } from "libraries/component/carbon-footprint/carbon-footprint.interface";
import { ILanguageCodeValue } from "libraries/pipes/localized/localized.interface";
import { ActionType, FareFamilyType } from "@afidium/ts-facade";
import { EInterfaceValues } from "libraries/component/interface-flag/interface-flag.component";

export enum EFareType {
    negoFare = 'NegoFare',
    public = 'Public',
    tourOperator = 'TourOperator',
    unknown = 'Unknown',
}


export enum ECriteria {
    carbonFootprint = "carbonFootprint",
    duration = "duration",
    price = "price",
    rating = "rating",
    sort = "sort",
}

export interface ICriteriaItem {
    criteria: ECriteria;
    bookingId: string;
}


export enum EContractXType {
    cost = 'Cost',
    retail = 'Retail',
}

export enum ETravellerType {
    adult = 'Adult',
    child = 'Child',
    infant = 'Infant',
}

export interface IGetAirTripQuotes {
    booking: IQuotes;
    bestQuotes: {
        booking: IQuotes;
        criteriaArray: ICriteriaItem[];
    };
    dictionary: IGetAirTripQuotesDictionary;
    key: string;
    quantity: {
        booking: number;
    };
    status: {
        severity: string;
    };
    filter: IAirQuoteFilter;
}

export interface IGetAirTripQuotesDictionary {
    aircraft: IAircraft;
    airport: IAirports;
    carrier: ICarrier;
    fareFamily: {[key: string]: IFareFamilyDictionary};
}

export interface IFareFamilyDictionary {
    code: string;
    xId: string;
    vendor: {
        iata: string;
        name: string;
    };
    name: ILanguageCodeValue;
    description?: IFareFamilyDescription;
    media: {
        imageArray: IFareFamilyImageArray[];
    };
};

export interface IFareFamilyDescription {
    index: string;
    languageCode: string;
    textLineArray: string[];
}

export interface IFareFamilyImageArray {
    url: string;
    dimension: {
        width: string;
        height: string;
        unit: string;
    }
}


export interface IGetAirTripQuote {
    booking: IQuotes;
    dictionary: {
        aircraft: IAircraft;
        airport: IAirports;
    };
    key: string;
    quantity: {
        booking: number;
    };
    status: {
        severity: string;
    };
}

export interface IAirports {
    [key: string]: IAirport;
}

export interface IAirport {
    airport: ICodeName;
    city: ICodeName;
    country: ICodeName;
}

export interface ICarrier {
    [key: string]: ICodeName;
}

export interface IAircraft {
    [key: string]: ICodeName;
}

export interface IQuotes {
    [key: string]: IQuote;
}

export interface ITravellers {
    [key: string]: ITravellerObject;
}

export interface IJourneysRefAndAirRating {
    journeyIds: string[];
    journeyKey: string;
    rating: IAirRating;
}

export interface IQuoteLuggage {
    is: string;
    rule: IRule[];
}

export interface IQuote {
    interface?: EInterfaceValues;
    fareType: EFareType[] | EFareType;
    luggage?: IQuoteLuggage;
    luggageArray?: IQuoteLuggage[];
    priceDifference?: number;
    segment: ISegments;
    total: ITotal[];
    traveller: ITravellers;
    price: IPrice[];
    type: string;
    fareFamily: {[key: string]: IFareFamily};
    currency?: IAirTripQuoteCurrency;
    validity: {
        end: string;
    };
    value: number;
    vendor: ICodeName;
    transactionId: string;
    host: string;
    title?: string;
    externalContextId: string;
    beginDaysDifference?: number | string;
    carbonFootprintArray?: IBookingCarbonFootprint[];
    ratingArray: IJourneysRefAndAirRating[];
    actionArray?: ActionType[];
    fareFamilyDictionary?: FareFamilyType[];
    exchange?: IQuoteExchange;
    cancellation?: IQuoteExchange;
    matchFareSentinel?: boolean;
}

export interface IQuoteExchange {
    rule : IQuoteExchangeRule[];
    calculation?: IQuoteExchangeRuleCalculation[];
}
export interface IQuoteExchangeRuleCalculation {
    for: string;
    unit: string;
}
export interface IQuoteExchangeRuleValidity {
    countingFrom: EQuoteExchangeRuleValidityCountingFrom;
    role: string;
}

export enum EQuoteExchangeRuleValidityCountingFrom {
    AfterDeparture = "AfterDeparture",
    BeforeDeparture = "BeforeDeparture",
    AfterBooking = "AfterBooking",
}

export interface IQuoteExchangeRule {
    penalty: number | IPercentage;
    calculation?: IQuoteExchangeRuleCalculation[];
    validity?: IQuoteExchangeRuleValidity;
}
export interface IPercentage {
    percentage: number;
}

export interface IBookingCarbonFootprint {
    carbonFootprint: ICarbonFootPrint;
    carbonFootPrint?: ICarbonFootPrint;
    journeyIds?: string[];
    journeyKey?: string;
}

export interface IPriceReference {
    base?: {
        reference: number;
    };
    surcharge?: {
        reference: number;
    };
    tax?: {
        reference: number;
    };
}

export interface IPriceDetail {
    value: number;
    amount: number;
    currency: string;
    decimals: number;
    traveller: ITravellerXId[];
    segment: ISegmentXId[];
    xUnitType: ETravellerType;
    quantity: string;
}

export interface ITravellerXId {
    xId: string;
}

export interface ISegmentXId {
    xId: string;
}

export interface ISegments {
    [key: string]: ISegment;
}

export interface ITotals {
    target: string;
    role: "Base" | "Tax" | "Total";
    total: number;
}

export interface ISegment {
    (key: string): ISegmentInformation;
    journey: string[];
    seat?: string[];
    journeyArray?: {
        xRef: string,
        syscode: string,
    }[];
    xId?: string;
}

/**
 * AKA Journey.
 */
export interface ISegmentInformation {
    begin: string;
    carrier: Array<{ code: string }>;
    duration: {
        minutes: number;
    };
    durationStop: {
        minutes: number;
    };
    end: string;
    from: string;
    sector: ISector[];
    stop: string;
    to: string;
    via: string[];
    rating? : IAirRating;
    carbonFootprint?: ICarbonFootPrint;
}

export interface ISector {
    xId?: string;
    aircraft?: string;
    interface?: EInterfaceValues;
    begin: string;
    carrier: {
        code: string;
    };
    duration: {
        minutes: number;
    };
    durationStop?: {
        minutes: number;
    };
    end: string;
    flight: string;
    from: string | IAirport;
    seat?: {
        [key: string]: {
            max?: number;
            status?: string;
            xCabin?: string;
        };
    };
    to: string | IAirport;
    xCategory?: SectorXCategory;
    carbonFootprint?: ICarbonFootPrint;
    fromTerminal?: string;
    toTerminal?: string;
    operatedBy?: {
        carrier: ICodeName;
    };
}

export enum SectorXCategory {
    scheduled = 'scheduled',
    lowcost = 'lowcost'
}

export interface IRule {
    calculation?: ICalculation[];
    in: string;
    restriction: IRestriction[];
    segment?: string[];
    traveller: string[];
}

export interface IRestriction {
    max: string;
    unit: string;
    topic: string;
}

export interface ICalculation {
    for: string;
    unit: string;
}

export interface IDetails {
    target: string;
    role: string;
    quantity: string;
    value: number;
    total: number;
    segment?: string[];
    traveller?: string[];
}

export interface ISectorLight {
    xId: string;
}

export interface ITraveller extends ISectorLight {
    xType: 'adult' | 'child' | 'infant';
}

export interface IAirTripQuoteCurrency {
    code: string;
    decimals: number;
}
